"use client";

import React from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { TaskAISummaryHeader } from "./TaskAISummaryHeader";
import { TaskAISummarySkeleton } from "./TaskAISummarySkeleton";
import { TaskAISummaryContent } from "./TaskAISummaryContent";
import type { TaskSummaryData } from "@/lib/types/ai";

export interface TaskAISummaryCompactProps {
  summary: TaskSummaryData | null;
  isLoading: boolean;
  isStreaming: boolean;
  error: string | null;
  displayedContent: string;
  isExpanded: boolean;
  statusMessage: string;
  showRefreshButton: boolean;
  showMetadata: boolean;
  taskNotes: Array<{ id: string }>;
  onRefresh: () => void;
  onGenerate: () => void;
  onClear: () => void;
  onToggleExpanded: () => void;
  className?: string;
}

/**
 * Compact variant of task AI summary
 * Minimal layout suitable for sidebars and compact views
 */
export function TaskAISummaryCompact({
  summary,
  isLoading,
  isStreaming,
  error,
  displayedContent,
  isExpanded,
  statusMessage,
  showRefreshButton,
  showMetadata,
  taskNotes,
  onRefresh,
  onGenerate,
  onClear,
  onToggleExpanded,
  className,
}: TaskAISummaryCompactProps) {
  const isGenerating = isLoading || isStreaming;

  return (
    <div className={cn("space-y-2", className)}>
      <TaskAISummaryHeader
        isLoading={isLoading}
        isStreaming={isStreaming}
        showRefreshButton={showRefreshButton}
        showClearButton={false}
        hasSummary={!!summary}
        onRefresh={onRefresh}
        onGenerate={onGenerate}
        onClear={onClear}
        compact
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}

      {isGenerating && <TaskAISummarySkeleton statusMessage={statusMessage} />}

      {displayedContent && (
        <div className="text-sm text-muted-foreground">
          <TaskAISummaryContent
            displayedContent={displayedContent}
            isExpanded={isExpanded}
            onToggleExpanded={onToggleExpanded}
            summary={summary}
            showMetadata={showMetadata}
          />
        </div>
      )}

      {!summary && !isGenerating && !error && (
        <Button
          variant="outline"
          size="sm"
          onClick={onGenerate}
          className="w-full h-11 sm:h-8"
        >
          <Sparkles className="h-3 w-3 mr-1" />
          Generate Summary
        </Button>
      )}
    </div>
  );
}
